<script setup>
import {onMounted, ref, watch} from 'vue';
import {codePreview, formatDate, validatePhoneNumber} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import Textarea from '@/components/forms/Textarea.vue';
import Select from '@/components/forms/Select.vue';
import {authData} from '@/services/AuthService';
import Input from "@/components/forms/Input.vue";
import Multiselect from '@vueform/multiselect';
import IntermediaryService from '@/services/IntermediaryService';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();

const props = defineProps({
  formsTemplate: {type: Object, required: false},
});

const formsTemplate = ref({name: '', version: '', public_name: '', type: 'FORM', description: '', data: '', is_active: true, confirmation_required: false, advisor_only: false, hide_document_from_client: false});
const availableFormsTemplatesTypes = ref([{value: 'FORM', label: t('createUpdateFormsTemplatesModal.form')}, {value: 'AML_QUESTIONNAIRE', label: t('createUpdateFormsTemplatesModal.aml_questionnaire')}, {value: 'INVESTMENT_QUESTIONNAIRE', label: t('createUpdateFormsTemplatesModal.investment_questionnaire')}, {value: 'MEETING_RECORD', label: t('createUpdateFormsTemplatesModal.meeting_records')}]);
const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);
const showIntermediaries = ref(false);
const intermediaryIds = ref(presetIntermediaries());
const intermediaries = ref([]);
const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      const formValue = formsTemplate.value;
      if (showIntermediaries.value) {
        formValue.intermediary_ids = intermediaryIds.value;
      } else {
        formValue.intermediary_ids = null;
      }
      emit('confirm', formValue);
      isOpen.value = false;
    } else {
      emit('cancel');
      isOpen.value = false;
    }
  }, 300);
}

if (props.formsTemplate) {
  formsTemplate.value = {...props.formsTemplate, data: JSON.stringify(props.formsTemplate.data, null, 2)};
}

function fetchIntermediaries() {
  IntermediaryService.getAll().then(response => {
    intermediaries.value = response.map(intermediary => {
      return {
        value: intermediary.id,
        label: intermediary.label
      }
    });
  });
}

function presetIntermediaries() {
  if (props.formsTemplate?.intermediary_ids?.length >= 0) {
    showIntermediaries.value = true;
    return props.formsTemplate?.intermediary_ids;
  }

  return [];
}

function openModal() {
  isOpen.value = true
}

onMounted(() => {
  fetchIntermediaries();
});
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div"  class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle v-if="formsTemplate.id" as="h3" class="modal-title">
                {{ t('createUpdateFormsTemplatesModal.form_templates_edit') }}
              </DialogTitle>
              <DialogTitle v-else as="h3" class="modal-title">
                {{ t('createUpdateFormsTemplatesModal.form_templates_create') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="row mb-3">
                  <div class="col-12 col-md-12 form-group">
                    <label for="nameInput" class="form-label">{{ t('createUpdateFormsTemplatesModal.name') }}</label>
                    <input v-model="formsTemplate.name" id="nameInput" class="form-control" required/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 col-md-12 form-group">
                    <label for="publicNameInput" class="form-label">{{ t('createUpdateFormsTemplatesModal.public_name') }}</label>
                    <input v-model="formsTemplate.public_name" id="publicNameInput" class="form-control" required/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="versionInput" class="form-label">{{ t('createUpdateFormsTemplatesModal.version') }}</label>
                    <input v-model="formsTemplate.version" id="versionInput" class="form-control"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="typeInput" class="form-label">{{ t('createUpdateFormsTemplatesModal.type') }}</label>
                    <Select v-model="formsTemplate.type" :options="availableFormsTemplatesTypes" class="w-100"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 col-md-6 form-group d-flex flex-column">
                    <label for="isActiveCheckbox" class="form-label">{{ t('createUpdateFormsTemplatesModal.active') }}</label>
                    <input v-model="formsTemplate.is_active" id="isActiveCheckbox" type="checkbox" class="form-check-input"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 col-md-6 form-group d-flex flex-column">
                    <label for="confirmationCheckbox" class="form-label">{{ t('createUpdateFormsTemplatesModal.require_client_signature') }}</label>
                    <input v-model="formsTemplate.confirmation_required" id="confirmationCheckbox" type="checkbox" class="form-check-input"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 col-md-6 form-group d-flex flex-column">
                    <label for="advisorOnlyCheckbox" class="form-label">{{ t('createUpdateFormsTemplatesModal.advisor_only') }}</label>
                    <input v-model="formsTemplate.advisor_only" id="advisorOnlyCheckbox" type="checkbox" class="form-check-input"/>
                  </div>
                  <div class="col-12 col-md-6 form-group d-flex flex-column">
                    <label for="hideDocumentFromClientCheckbox" class="form-label">{{ t('createUpdateFormsTemplatesModal.hide_document_from_client') }}</label>
                    <input v-model="formsTemplate.hide_document_from_client" id="hideDocumentFromClientCheckbox" type="checkbox" class="form-check-input"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 col-md-12 form-group">
                    <label for="descriptionInput" class="form-label">{{ t('createUpdateFormsTemplatesModal.description') }}</label>
                    <input v-model="formsTemplate.description" id="descriptionInput" class="form-control"/>
                  </div>
                </div>
                <div class="mb-3">
                  <div class="form-check">
                    <input type="checkbox" v-model="showIntermediaries" id="showIntermediariesCheck" class="form-check-input" />
                    <label for="showIntermediariesCheck" class="form-check-label">{{ t('createUpdateFormsTemplatesModal.template_for_certain_intermediaries') }}</label>
                  </div>
                  <div v-if="showIntermediaries" class="mb-3">
                    <div class="form-group">
                      <label for="intermediariesSelect" class="form-label">{{ t('createUpdateFormsTemplatesModal.intermediaries') }}</label>
                      <Multiselect id="intermediariesSelect" v-model="intermediaryIds" :options="intermediaries" :close-on-select="false" :hide-selected="false" :placeholder="t('createUpdateFormsTemplatesModal.choose_intermediaries')" mode="tags"/>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 form-group">
                    <label for="dataInput" class="form-label">{{ t('createUpdateFormsTemplatesModal.data') }}</label>
                    <textarea v-model="formsTemplate.data" id="dataInput" class="form-control" type="text" rows="50"/>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createUpdateFormsTemplatesModal.cancel') }}</button>
                <button class="btn btn-primary btn-md" type="submit">{{ t('createUpdateFormsTemplatesModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

